import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { householdService } from '@/services';
import { useHouseholdStore } from '@/stores';
import { useUserStore } from '@/stores/userStore';

/**
 * Whether a signed-in account that finished onboarding belongs to NO family.
 *
 * Leaving your only household (YAR-501), being removed from every household,
 * or the household being deleted leaves `users.onboarding_completed_at` set,
 * so every gate used to wave the account through to /today — an empty app
 * with no way out. Such an account must go back to the create-or-join fork.
 *
 * Getting this wrong the other way is worse: sending a parent who HAS a
 * family to the fork lets them create a second household by mistake. Boot
 * resolution publishes `householdId: null` both for "owns nothing" and for
 * "the lookup failed" — and for a user with a family the check below arms
 * precisely in the failure case. So `'none'` requires a positive, successful
 * answer from the database:
 *
 *   - the account's own `users.id` must be known (`useUserStore.userId`, set
 *     from the profile before auth loading clears) — without it, no check;
 *   - `getMyHouseholds(dbUserId)` THROWS on any query error and returns `[]`
 *     only when the query succeeded with zero active memberships. (The
 *     `…ForCurrentUser` variant returned `[]` when `auth.getUser()` failed on
 *     the network, which read as "no family" — the defect this replaces.)
 *
 *   - there must be an auth session. A token refresh that failed on the
 *     network keeps the user locally but drops the session, and supabase-js
 *     then queries as `anon` — which reads zero rows and would look exactly
 *     like "no family". No session ⇒ no answer.
 *
 * Any error, a missing id, or anything not yet known is `'unknown'`, which
 * every caller treats as today's behaviour: never the fork.
 *
 * One answer is shared by every consumer (ProtectedRoute and Onboarding both
 * ask) through a short-lived module cache keyed by the user id, so the two
 * gates cannot disagree and bounce the user between them, and only one
 * request is made.
 *
 *   'unknown'  — not applicable, not resolved yet, no user id, or the check failed
 *   'checking' — the store says no household; asking the database
 *   'has'      — the account belongs to at least one household
 *   'none'     — confirmed: no active household anywhere
 */
export type HouseholdlessState = 'unknown' | 'checking' | 'has' | 'none';

type Answer = 'has' | 'none' | 'error';

const CACHE_TTL_MS = 10_000;
const cache = new Map<string, { at: number; answer: Promise<Answer> }>();

function askOnce(dbUserId: string): Promise<Answer> {
  const hit = cache.get(dbUserId);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.answer;
  const answer = supabase.auth
    .getSession()
    .then(async ({ data }): Promise<Answer> => {
      if (!data.session) return 'error';
      const households = await householdService.getMyHouseholds(dbUserId);
      return households.length > 0 ? 'has' : 'none';
    })
    .catch((): Answer => 'error');
  cache.set(dbUserId, { at: Date.now(), answer });
  return answer;
}

/** Test seam and post-change reset: forget every cached answer. */
export function resetHouseholdlessCache(): void {
  cache.clear();
}

export function useHouseholdlessAccount(enabled: boolean): HouseholdlessState {
  const resolved = useHouseholdStore((s) => s.resolved);
  const householdId = useHouseholdStore((s) => s.householdId);
  const dbUserId = useUserStore((s) => s.userId);
  const needsCheck =
    enabled && resolved && householdId === null && dbUserId !== null;
  const [answer, setAnswer] = useState<{
    key: string;
    value: Answer;
  } | null>(null);

  useEffect(() => {
    if (!needsCheck || dbUserId === null) return;
    let active = true;
    void askOnce(dbUserId).then((value) => {
      if (active) setAnswer({ key: dbUserId, value });
    });
    return () => {
      active = false;
    };
  }, [needsCheck, dbUserId]);

  if (!enabled || !resolved) return 'unknown';
  if (householdId !== null) return 'has';
  if (dbUserId === null) return 'unknown';
  if (answer === null || answer.key !== dbUserId) return 'checking';
  if (answer.value === 'error') return 'unknown';
  return answer.value;
}
